import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Platform } from "../types";
import {
  ExternalLink,
  Clock,
  Image as ImageIcon,
  Video,
  Filter,
  ChevronDown,
  RotateCcw,
  Trash2,
  Loader,
  ChevronUp,
} from "lucide-react";
import { historyRefreshService } from "../services/historyRefreshService";
import { getPlatformIcon } from "../utils/platformIcons";
import API from "../services/api";
import { useTranslation } from "react-i18next";
import { useAppContext } from "@/context/AppContext";
import { notify } from "@/utils/toast";
import { PostHistorySkeleton } from "@/components/Skeletons";

export interface HistoryPageRef {
  refreshHistory: () => Promise<void>;
}

interface PostHistoryItem {
  id: string;
  platform: Platform;
  postId: string;
  postUrl: string;
  content: string;
  publishedAt: string;
  isRead: boolean;
  mediaUrl?: string;
  mediaType?: "image" | "video";
  thumbnailUrl?: string;
  metadata?: {
    title?: string;
    description?: string;
    image?: string;
    thumbnail?: string;
    platform_image?: string;
    likes?: number;
    shares?: number;
    comments?: number;
  };
}

type ReadFilter = "all" | "read" | "unread";
type PlatformFilter = "all" | Platform;
type TimePeriod = "all" | "today" | "week" | "month";
type SortBy = "date_desc" | "date_asc" | "platform_asc" | "platform_desc";

export const HistoryPage = forwardRef<HistoryPageRef>((props, ref) => {
  const { setUnreadCount, state, postHistory, fetchPostHistory } = useAppContext();

  // Extract data and pagination from context
  const posts = useMemo(() => {
    if (Array.isArray(postHistory)) return postHistory;
    return postHistory?.data || [];
  }, [postHistory]);

  const pagination = useMemo(() => {
    if (postHistory?.pagination) {
      return postHistory.pagination;
    }
    return {
      page: 1,
      limit: 10,
      totalPosts: 0,
      totalPages: 1,
      hasMore: false,
    };
  }, [postHistory]);

  const isLoadingHistory = state.postHistoryLoading;

  const { t } = useTranslation();

  // Filter states
  const [readFilter, setReadFilter] = useState<ReadFilter>("all");
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>("all");
  const [timePeriod, setTimePeriod] = useState<TimePeriod>("all");
  const [sortBy, setSortBy] = useState<SortBy>("date_desc");
  const [showFilters, setShowFilters] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Expose refresh method to parent
  const refreshHistory = useCallback(async () => {
    await fetchPostHistory("published", 1, false);
  }, [fetchPostHistory]);

  useImperativeHandle(ref, () => ({
    refreshHistory,
  }));

  // Reset to first page when filters change
  useEffect(() => {
    fetchPostHistory("published", 1, false);
  }, [readFilter, platformFilter, timePeriod, sortBy, fetchPostHistory]);

  // Scroll-to-top visibility
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setShowScrollTop(container.scrollTop > 400);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Mark as read
  const markAsRead = async (postId: string) => {
    try {
      await API.readHistoryById(postId);
      setUnreadCount(0);
      historyRefreshService.refreshHistory();
    } catch (error) {
      console.error("Error marking post as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      setUnreadCount(0);
      await API.readAllHistory();
      await historyRefreshService.refreshHistory();
      await fetchPostHistory("published", 1, false);
    } catch (error) {
      notify("error", t("failed_mark_all_posts_read"));
    }
  };

  const resetFilters = () => {
    setReadFilter("all");
    setPlatformFilter("all");
    setTimePeriod("all");
    setSortBy("date_desc");
  };

  const hasActiveFilters =
    readFilter !== "all" ||
    platformFilter !== "all" ||
    timePeriod !== "all" ||
    sortBy !== "date_desc";

  // Filtering and sorting logic (memoized)
  const { filteredPosts, unreadCount, availablePlatforms } = useMemo(() => {
    if (!posts?.length) {
      return { filteredPosts: [], unreadCount: 0, availablePlatforms: [] };
    }

    const now = new Date();
    let filterDate: Date | null = null;

    if (timePeriod === "today") {
      filterDate = new Date(now.setHours(0, 0, 0, 0));
    } else if (timePeriod === "week") {
      filterDate = new Date(now);
      filterDate.setDate(now.getDate() - 7);
    } else if (timePeriod === "month") {
      filterDate = new Date(now);
      filterDate.setMonth(now.getMonth() - 1);
    }

    const result: PostHistoryItem[] = [];
    let unread = 0;
    const platformSet = new Set<string>();

    for (const post of posts) {
      if (!post.isRead) unread++;
      if (post.platform) platformSet.add(post.platform);

      if (readFilter === "read" && !post.isRead) continue;
      if (readFilter === "unread" && post.isRead) continue;
      if (platformFilter !== "all" && post.platform !== platformFilter) continue;
      if (filterDate && new Date(post.publishedAt) < filterDate) continue;

      result.push(post);
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case "date_asc":
          return new Date(a.publishedAt).getTime() - new Date(b.publishedAt).getTime();
        case "date_desc":
          return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
        case "platform_asc":
          return a.platform.localeCompare(b.platform);
        case "platform_desc":
          return b.platform.localeCompare(a.platform);
        default:
          return 0;
      }
    });

    return {
      filteredPosts: result,
      unreadCount: unread,
      availablePlatforms: Array.from(platformSet),
    };
  }, [posts, readFilter, platformFilter, timePeriod, sortBy]);

  // Delete handler
  const deleteHistory = async (id: string) => {
    if (deletingId === id) return;
    setDeletingId(id);
    try {
      await API.deleteHistory(id);
      notify("success", "Post deleted successfully");
      await fetchPostHistory("published", 1, false);
    } catch (error) {
      notify("error", "Failed to delete post");
    } finally {
      setDeletingId(null);
    }
  };

  // Helper functions for rendering
  const renderPlatformIcon = (platform: string) => {
    const IconComponent = getPlatformIcon(platform as any);
    if (!IconComponent) {
      return (
        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-600">
          <span className="text-white text-xs font-bold">
            {platform?.slice(0, 2).toUpperCase()}
          </span>
        </div>
      );
    }
    return (
      <div className="w-8 h-8 flex items-center justify-center rounded-full">
        <IconComponent className="w-6 h-6" />
      </div>
    );
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const getThumbnailUrl = (post: PostHistoryItem) =>
    post?.thumbnailUrl ||
    post?.metadata?.thumbnail ||
    post?.metadata?.image ||
    post?.mediaUrl ||
    null;

  const getMediaType = (post: PostHistoryItem): "image" | "video" | "text" => {
    if (post.mediaType) return post.mediaType;
    const url = getThumbnailUrl(post)?.toLowerCase() || "";
    if (!url) return "text";
    if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/)) return "image";
    if (url.match(/\.(mp4|webm|ogg|mov|avi|wmv)$/)) return "video";
    if (url.includes("youtube.com") || url.includes("youtu.be")) return "video";
    return "image";
  };

  const renderThumbnail = (post: PostHistoryItem) => {
    const thumbnailUrl = getThumbnailUrl(post);
    const mediaType = getMediaType(post);

    if (!thumbnailUrl) {
      if (mediaType === "text") return null;
      const Icon = mediaType === "video" ? Video : ImageIcon;
      return (
        <div className="flex-shrink-0 w-32 h-24 bg-gray-100 border-r border-gray-200 flex items-center justify-center">
          <Icon className="w-8 h-8 text-gray-400" />
        </div>
      );
    }

    return (
      <div className="flex-shrink-0 w-32 p-1 bg-gray-300 border-r border-gray-200 relative">
        <img
          src={thumbnailUrl}
          alt="Post thumbnail"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = "";
            e.currentTarget.style.display = "none";
            const parent = e.currentTarget.parentElement;
            if (parent) {
              parent.innerHTML = `
                <div class="w-full h-full bg-gray-100 flex items-center justify-center">
                  <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    ${
                      mediaType === "video"
                        ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>'
                        : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>'
                    }
                  </svg>
                </div>
              `;
            }
          }}
        />
        {mediaType === "video" && (
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
            <div className="w-10 h-10 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
              <div className="w-0 h-0 border-l-5 border-r-0 border-t-2 border-b-2 border-transparent border-l-gray-800 ml-1"></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getHostname = (url: string) => {
    try {
      return new URL(url).hostname.replace(/^www\./, "");
    } catch {
      return url;
    }
  };

  // Initial loading state
  if (isLoadingHistory && !posts.length) {
    return (
      <div className="h-fit p-4 py-2">
        <div className="max-w-5xl mx-auto mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">{t("post_history")}</h2>
        </div>
        <PostHistorySkeleton />
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      id="scrollContainer"
      className="h-[calc(100vh-120px)] overflow-y-auto relative scroll-smooth"
    >
      <div className="p-4 py-2">
        <div className="max-w-5xl mx-auto mb-6">
          <div className="flex justify-between items-center gap-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{t("post_history")}</h2>
            <div className="flex items-center gap-3">
              {pagination.hasMore && (
                <span className="text-sm text-gray-500">
                  {posts.length} / {pagination.totalPosts || posts.length}
                </span>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-md border transition-colors ${
                  showFilters || hasActiveFilters
                    ? "bg-blue-50 border-blue-200 text-blue-700"
                    : "bg-white border-purple-600 text-purple-600"
                }`}
              >
                <Filter className="w-3 h-3" />
                {t("filters")}
                {hasActiveFilters && (
                  <span className="ml-1 w-2 h-2 bg-blue-600 rounded-full"></span>
                )}
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    showFilters ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="w-full mb-6 p-4 bg-white border border-gray-200 rounded-md">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Read Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("read_status")}
                  </label>
                  <select
                    value={readFilter}
                    onChange={(e) => setReadFilter(e.target.value as ReadFilter)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:ring-blue-600 focus:border-blue-500"
                  >
                    <option value="all">{t("all_posts")}</option>
                    <option value="unread">
                      {t("unread")} ({unreadCount})
                    </option>
                    <option value="read">
                      {t("read")} ({posts?.length - unreadCount})
                    </option>
                  </select>
                </div>

                {/* Platform Filter */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("platform")}
                  </label>
                  <select
                    value={platformFilter}
                    onChange={(e) => setPlatformFilter(e.target.value as PlatformFilter)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:ring-blue-600 focus:border-blue-500"
                  >
                    <option value="all">{t("all_platforms")}</option>
                    {availablePlatforms?.map((platform: any) => (
                      <option key={platform} value={platform}>
                        {platform?.charAt(0).toUpperCase() + platform?.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("time_period")}
                  </label>
                  <select
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:ring-blue-600 focus:border-blue-500"
                  >
                    <option value="all">{t("all_time")}</option>
                    <option value="today">{t("today")}</option>
                    <option value="week">{t("last_7_days")}</option>
                    <option value="month">{t("last_30_days")}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("sort_by")}
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortBy)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:ring-blue-600 focus:border-blue-500"
                  >
                    <option value="date_desc">{t("newest_first")}</option>
                    <option value="date_asc">{t("oldest_first")}</option>
                    <option value="platform_asc">{t("platform_a_z")}</option>
                    <option value="platform_desc">{t("platform_z_a")}</option>
                  </select>
                </div>
              </div>

              {hasActiveFilters && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={resetFilters}
                    className="flex items-center gap-2 text-sm px-3 py-1 text-gray-500 font-medium hover:text-slate-800 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    {t("reset_filters")}
                  </button>
                </div>
              )}
            </div>
          )}

          {hasActiveFilters && (
            <div className="mb-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                <span>{t("showing")}:</span>
                {readFilter !== "all" && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                    {readFilter === "read" ? "Read posts" : "Unread posts"}
                  </span>
                )}
                {platformFilter !== "all" && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                    {platformFilter.charAt(0).toUpperCase() + platformFilter.slice(1)}
                  </span>
                )}
                {timePeriod !== "all" && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                    {timePeriod === "today"
                      ? "Today"
                      : timePeriod === "week"
                      ? "Last 7 days"
                      : "Last 30 days"}
                  </span>
                )}
                <span className="text-gray-500 font-medium">
                  • {filteredPosts?.length} posts
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Infinite Scroll Container */}
        <div className="w-full mx-auto">
          {filteredPosts?.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-purple-600 mb-4">
                <Clock className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {hasActiveFilters ? t("no_posts_match") : t("no_posts_yet")}
              </h3>
              <p className="text-gray-500 font-medium">
                {hasActiveFilters ? t("adjust_filters") : t("published_posts_here")}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <InfiniteScroll
              dataLength={filteredPosts.length}
              next={() => fetchPostHistory("published", pagination.page + 1, true)}
              hasMore={pagination.hasMore}
              loader={<PostHistorySkeleton />}
              scrollableTarget="scrollContainer"
              endMessage={
                <p className="text-center text-gray-400 py-6">
                  {t("no_more_posts")}
                </p>
              }
            >
              <div className="space-y-6">
                {filteredPosts?.map((post: any) => (
                  <div
                    key={post?.id}
                    className={`bg-white border rounded-md overflow-hidden hover:shadow-md transition-all duration-200 ${
                      !post?.isRead ? "bg-blue-50/30" : "border-gray-100"
                    }`}
                  >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        {renderPlatformIcon(post.platform)}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">
                              {post.platform}
                            </span>
                            {!post.isRead && (
                              <span className="w-2.5 h-2.5 bg-blue-600 rounded-full" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <Clock className="w-3.5 h-3.5" />
                            {formatTimeAgo(post.publishedAt)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => deleteHistory(post.id)}
                          disabled={deletingId === post.id}
                          className="p-2 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50 transition disabled:opacity-50"
                          title="Delete"
                        >
                          {deletingId === post.id ? (
                            <Loader className="animate-spin transition-all w-5 h-5" />
                          ) : (
                            <Trash2 className="w-5 h-5" />
                          )}
                        </button>
                        <a
                          href={post.postUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-500 hover:text-blue-600 rounded-full hover:bg-blue-50 transition"
                          title="View original"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </a>
                      </div>
                    </div>

                    <a
                      href={post.postUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => !post.isRead && markAsRead(post.id)}
                      className="block hover:bg-gray-50/70 transition-colors"
                    >
                      <div className="flex">
                        {renderThumbnail(post)}
                        <div className="flex-1 p-4">
                          <p className="text-gray-600 text-sm line-clamp-2 mb-2">
                            {post.metadata?.description || post.content}
                          </p>
                          <p className="text-xs text-gray-500">
                            {post.platform} • {post.postUrl && getHostname(post.postUrl)}
                          </p>
                        </div>
                      </div>
                    </a>
                  </div>
                ))}
              </div>
            </InfiniteScroll>
          )}
        </div>
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-3.5 bg-blue-600 text-white rounded-full shadow-xl hover:bg-blue-700 transition-all hover:scale-110 z-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
          aria-label="Scroll to top"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
});

HistoryPage.displayName = "HistoryPage";